import { useState, useEffect, useMemo } from 'react'
import { Check, X, Loader2, AtSign } from 'lucide-react'
import { agentService } from '../services/agentService'

// Simple debounce implementation
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function debounce<T extends (...args: any[]) => any>(func: T, wait: number) {
    let timeout: ReturnType<typeof setTimeout>
    return (...args: Parameters<T>) => {
        clearTimeout(timeout)
        timeout = setTimeout(() => func(...args), wait)
    }
}

interface HandleInputProps {
    value: string
    onChange: (value: string) => void
    error?: string
    onError: (error: string | undefined) => void
}

export function HandleInput({ value, onChange, error, onError }: HandleInputProps) {
    const [isChecking, setIsChecking] = useState(false)
    const [isAvailable, setIsAvailable] = useState<boolean | null>(null)
    const [suggestions, setSuggestions] = useState<string[]>([])

    // Regex for validation: lowercase, numbers, underscores, hyphens, 3-30 chars
    const handleRegex = useMemo(() => /^[a-z0-9_-]{3,30}$/, [])

    const checkAvailability = useMemo(
        () => debounce(async (handle: string) => {
            if (!handle || !/^[a-z0-9_-]{3,30}$/.test(handle)) {
                setIsChecking(false)
                return
            }

            setIsChecking(true)
            try {
                const available = await agentService.checkHandleAvailability(handle)
                setIsAvailable(available)

                if (!available) {
                    onError('Este handle já está em uso')
                    const newSuggestions = agentService.generateHandleSuggestions(handle, handle)
                    setSuggestions(newSuggestions)
                } else {
                    onError(undefined)
                    setSuggestions([])
                }
            } catch (err) {
                console.error('Error checking handle:', err)
            } finally {
                setIsChecking(false)
            }
        }, 300),
        [onError]
    )

    useEffect(() => {
        if (!value) {
            setIsAvailable(null)
            setSuggestions([])
            return
        }

        // Immediate format validation
        if (!handleRegex.test(value)) {
            onError('Use apenas letras minúsculas, números, _ e - (3-30 caracteres)')
            setIsAvailable(false)
            return
        }

        // Trigger availability check
        setIsAvailable(null) // Reset status while waiting for debounce
        checkAvailability(value)
    }, [value, checkAvailability, onError, handleRegex])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '')
        onChange(newValue)
    }

    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Handle (ID Único) <span className="text-red-500">*</span>
            </label>

            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <AtSign className="h-5 w-5 text-gray-400" />
                </div>

                <input
                    type="text"
                    data-testid="agent-handle-input"
                    value={value}
                    onChange={handleInputChange}
                    className={`
                        block w-full pl-10 pr-10 py-2.5 sm:text-sm rounded-xl border
                        bg-white dark:bg-gray-900 text-gray-900 dark:text-white
                        focus:ring-2 focus:ring-offset-0 transition-all
                        ${error
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                            : isAvailable
                                ? 'border-green-300 focus:border-green-500 focus:ring-green-500/20'
                                : 'border-gray-300 dark:border-gray-700 focus:border-purple-500 focus:ring-purple-500/20'
                        }
                    `}
                    placeholder="nome_do_agente"
                />

                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    {isChecking ? (
                        <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                    ) : isAvailable === true ? (
                        <Check className="h-5 w-5 text-green-500" />
                    ) : isAvailable === false || error ? (
                        <X className="h-5 w-5 text-red-500" />
                    ) : null}
                </div>
            </div>

            {error && (
                <p className="text-sm text-red-500 animate-in slide-in-from-top-1">{error}</p>
            )}

            {!isAvailable && suggestions.length > 0 && (
                <div className="animate-in slide-in-from-top-2 fade-in duration-300">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Sugestões disponíveis:</p>
                    <div className="flex flex-wrap gap-2">
                        {suggestions.map((suggestion) => (
                            <button
                                key={suggestion}
                                type="button"
                                onClick={() => onChange(suggestion)}
                                className="px-3 py-1 text-xs font-medium rounded-full bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors"
                            >
                                @{suggestion}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}
